// @flow

/* eslint react/jsx-no-bind:0 */

import React, { Component } from 'react';
import DeepLink from 'app/common/react/components/growth/unauth/lib/DeepLink';
import i18n from 'app/common/lib/i18n';
import PropTypes from 'prop-types';
import url from 'url';
import urlUtil from 'app/common/lib/urlUtil';
import { buildExploreDeeplinks } from 'app/common/lib/deeplinkRoutes';
import { capitalized } from 'app/common/lib/StringUtil';
import { getPinMeasurements } from 'app/common/react/components/growth/unauth/pin/pinUtils';
import { Icon } from 'gestalt';
import { track } from 'app/common/lib/react/analytics';
import type {
  AnnotationWithLink,
  AnnotationsWithLinks,
} from 'app/common/react/components/growth/unauth/lib/PropTypes';

type Props = {
  annotationsWithLinks?: AnnotationsWithLinks,
  carouselTagColor?: ?string,
  openInCurrentTab: boolean,
  disableDeeplinks?: boolean,
  image_signature?: string,
  inRemoveInlineGroup?: boolean,
  styleOverrides?: {
    gradientLeft?: {
      background: string,
    },
    gradientRight?: {
      background: string,
    },
  },
};

type State = {
  scrollTo: number,
  scrollMin: boolean,
  scrollMax: boolean,
};

const RIGHT = 'RIGHT';
const LEFT = 'LEFT';
const pinMeasurements = getPinMeasurements();
const nullifyClick = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const styles = {
  gradientLeft: {
    display: 'block',
    background: '-webkit-linear-gradient(left, rgba(255,255,255,1) 25%,rgba(255,255,255,0) 100%)',
    height: '30px',
    left: '0px',
    top: '0px',
    position: 'absolute',
    width: '45px',
    lineHeight: '25px',
    paddingLeft: '4px',
    paddingTop: '2px',
  },
  gradientRight: {
    background: '-webkit-linear-gradient(right, rgba(255,255,255,1) 25%,rgba(255,255,255,0) 100%)',
    height: '30px',
    right: '0px',
    top: '0px',
    position: 'absolute',
    width: '45px',
    textAlign: 'right',
    lineHeight: '25px',
    paddingRight: '4px',
    paddingTop: '2px',
  },
  carouselWrapper: {
    overflow: 'scroll',
    whiteSpace: 'nowrap',
    padding: '3px 0 22px 0',
  },
  carouselContainer: {
    height: pinMeasurements.vaseCarouselHeight + 'px',
    overflow: 'hidden',
    position: 'relative',
  },
  carouselContainerRemoveInlineStyle: {
    height: pinMeasurements.vaseCarouselHeight + 'px',
  },
  carouselTag: {
    backgroundColor: '#ebebe8',
    borderRadius: '5px',
    color: '#333',
    display: 'inline-block',
    fontSize: '9px',
    fontWeight: 'bold',
    margin: '0 5px 0 0',
    padding: '7px 7px',
    whiteSpace: 'nowrap',
    wordWrap: 'break-word',
  },
  carouselTagUnclickable: {
    cursor: 'default',
  },
};

class VaseCarousel extends Component<Props, State> {
  state: State = {
    scrollTo: 0,
    scrollMin: true,
    scrollMax: false,
  };

  componentDidMount() {
    this.wrapper.addEventListener('scroll', this.handleScroll.bind(this));
  }

  componentDidUpdate() {
    this.wrapper.scrollLeft = this.state.scrollTo;
  }

  componentWillUnmount() {
    this.wrapper.removeEventListener('scroll', this.handleScroll.bind(this));
  }

  tags: Array<HTMLElement> = [];
  wrapper: HTMLElement;

  handleTagClick(interestName: string, e: SyntheticEvent<>) {
    const {
      currentUrl,
      pageType,
      userAgent: { raw, platform },
    } = this.context;

    track(PCONST.thrift.EventType.CLICK, {
      element: PCONST.thrift.ElementType.VASE_TAG,
      component: PCONST.thrift.ComponentType.VASE_TAGS_WRAPPER,
      unauth_vase_tag: interestName,
      unauth_vase_tag_image_sig: this.props.image_signature,
      unauth_page_type: pageType,
      unauth_page_id: urlUtil.getUnauthPageId(pageType, currentUrl),
    });
    const deepLink = new DeepLink(raw, platform);
    if (
      !this.props.disableDeeplinks &&
      deepLink.shouldDeepLink(document.referrer, this.context.userAgent, false)
    ) {
      e.preventDefault();
      deepLink.navigateToDeepLink(this.context.unauthId, buildExploreDeeplinks(interestName));
    }
    e.stopPropagation();
  }

  handleScroll() {
    // need to round scrollLeft, or it doesn't line up with bounds when zoomed
    const scrollLeft = Math.round(this.wrapper.scrollLeft);
    const scrollRight = scrollLeft + this.wrapper.clientWidth;

    const scrollMin = scrollLeft === 0;
    const scrollMax = scrollRight >= this.wrapper.scrollWidth;

    this.setState({ scrollTo: scrollLeft, scrollMin, scrollMax });
  }

  handleArrowClick(direction: string, event: SyntheticEvent<>) {
    event.preventDefault();
    event.stopPropagation();

    // extra spacing due to margin + padding of the tag bubble
    const spacingLeft = 12;
    const getTagOffset = (el) => el.offsetLeft - spacingLeft;

    // need to round scrollLeft, or it doesn't line up with bounds when zoomed
    const currentScrollOffset = Math.round(this.wrapper.scrollLeft);
    const tagOffsets = this.tags.map(getTagOffset);

    // add some "wiggle" room to our calculation
    const wiggle = 4;
    let offset;
    if (direction === RIGHT) {
      // scroll right: find first edge that is greater than current scroll
      // position and scroll to it.
      offset = tagOffsets.find((tagOffset) => tagOffset > currentScrollOffset + wiggle);
    } else if (direction === LEFT) {
      // scroll left: find last edge that is less than current scroll position
      // and scroll to it.
      offset = tagOffsets.reverse().find((tagOffset) => tagOffset < currentScrollOffset - wiggle);
    }

    this.setState({
      scrollTo: offset,
    });
  }

  renderPlainTag(
    annotation: string,
    index: number,
    appendTagRef: Function,
    inRemoveInlineGroup?: boolean,
  ) {
    let carouselTagStyle = {};
    if (inRemoveInlineGroup) {
      carouselTagStyle = this.props.carouselTagColor
        ? { backgroundColor: this.props.carouselTagColor }
        : null;
    } else {
      carouselTagStyle = this.props.carouselTagColor
        ? { ...styles.carouselTag, backgroundColor: this.props.carouselTagColor }
        : styles.carouselTag;
    }

    // Capture click so the ancestor element's click handler isn't triggered.
    return (
      <a
        href=""
        key={index}
        onClick={nullifyClick}
        ref={appendTagRef.bind(this, index)}
        className="vaseCarousel_vaseTagLink"
        style={{ ...carouselTagStyle, ...styles.carouselTagUnclickable }}
      >
        {capitalized(annotation)}
      </a>
    );
  }

  renderTagWithLink(
    annotationWithLink: AnnotationWithLink,
    index: number,
    appendTagRef: Function,
    inRemoveInlineGroup?: boolean,
  ) {
    const currentPath = this.context.currentUrl
      ? url.parse(this.context.currentUrl).pathname || ''
      : '';
    let carouselTagStyle = {};
    if (inRemoveInlineGroup) {
      carouselTagStyle = this.props.carouselTagColor
        ? { backgroundColor: this.props.carouselTagColor }
        : null;
    } else {
      carouselTagStyle = this.props.carouselTagColor
        ? { ...styles.carouselTag, backgroundColor: this.props.carouselTagColor }
        : styles.carouselTag;
    }

    if (!annotationWithLink.name) {
      return null;
    }

    if (currentPath === annotationWithLink.url || !annotationWithLink.url) {
      return this.renderPlainTag(annotationWithLink.name, index, appendTagRef, inRemoveInlineGroup);
    }

    return (
      <a
        style={carouselTagStyle}
        href={annotationWithLink.url}
        key={index}
        onClick={(e: SyntheticEvent<>) => this.handleTagClick(annotationWithLink.name, e)}
        ref={appendTagRef.bind(this, index)}
        className="vaseCarousel_vaseTagLink"
        target={this.props.openInCurrentTab || inRemoveInlineGroup ? '_self' : '_blank'}
      >
        {capitalized(annotationWithLink.name)}
      </a>
    );
  }

  renderTags(inRemoveInlineGroup?: boolean) {
    const { annotationsWithLinks } = this.props;
    // ref callback to generate list of tags ...
    const appendTagRef = (index, el) => {
      this.tags[index] = el;
    };

    if (annotationsWithLinks && annotationsWithLinks.length > 0) {
      // $FlowFixMe: new errors found from flow 0.96 upgrade
      return annotationsWithLinks.map((annotationWithLink, index) =>
        this.renderTagWithLink(annotationWithLink, index, appendTagRef, inRemoveInlineGroup),
      );
    } else {
      return null;
    }
  }

  render() {
    const { styleOverrides, inRemoveInlineGroup } = this.props;
    let gradientLeft = {};
    let gradientRight = {};

    if (inRemoveInlineGroup) {
      gradientLeft =
        styleOverrides && styleOverrides.gradientLeft ? { ...styleOverrides.gradientLeft } : null;
      gradientRight =
        styleOverrides && styleOverrides.gradientRight ? { ...styleOverrides.gradientRight } : null;
    } else {
      gradientLeft =
        styleOverrides && styleOverrides.gradientLeft
          ? { ...styles.gradientLeft, ...styleOverrides.gradientLeft }
          : { ...styles.gradientLeft };
      gradientRight =
        styleOverrides && styleOverrides.gradientRight
          ? { ...styles.gradientRight, ...styleOverrides.gradientRight }
          : { ...styles.gradientRight };
    }

    return (
      <div
        className={inRemoveInlineGroup ? 'vaseCarousel_vasetags_container' : ''}
        style={
          inRemoveInlineGroup ? styles.carouselContainerRemoveInlineStyle : styles.carouselContainer
        }
        data-test-id="vasetags"
      >
        <div
          ref={(el) => {
            // $FlowFixMe: New error after Flow Upgrade http://pinch.pinadmin.com/flow53upgdoc
            this.wrapper = el;
          }}
          className={inRemoveInlineGroup ? 'vaseCarousel_vasetags_wrapper' : ''}
          style={inRemoveInlineGroup ? null : styles.carouselWrapper}
        >
          {this.renderTags(inRemoveInlineGroup)}
          {this.state.scrollMin ? null : (
            <a
              href=""
              onClick={this.handleArrowClick.bind(this, LEFT)}
              style={gradientLeft}
              className={'VaseCarousel__button VaseCarousel__buttonLeft'}
            >
              <Icon
                accessibilityLabel={i18n._('Back', 'Accessible label for the carousel back icon')}
                icon="arrow-back"
                inline
                size={8}
              />
            </a>
          )}
          {this.state.scrollMax ? null : (
            <a
              href=""
              onClick={this.handleArrowClick.bind(this, RIGHT)}
              style={gradientRight}
              className={'VaseCarousel__button VaseCarousel__buttonRight'}
            >
              <Icon
                accessibilityLabel={i18n._(
                  'Forward',
                  'Accessible label for the carousel forward icon',
                )}
                icon="arrow-forward"
                inline
                size={8}
              />
            </a>
          )}
        </div>
      </div>
    );
  }
}

VaseCarousel.propTypes = {
  annotationsWithLinks: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  disableDeeplinks: PropTypes.bool,
  openInCurrentTab: PropTypes.bool,
  carouselTagColor: PropTypes.string,
  image_signature: PropTypes.string,
  inRemoveInlineGroup: PropTypes.bool,
  styleOverrides: PropTypes.shape({}),
};

VaseCarousel.contextTypes = {
  currentUrl: PropTypes.string.isRequired,
  pageType: PropTypes.string.isRequired,
  userAgent: PropTypes.shape({
    raw: PropTypes.string.isRequired,
    platform: PropTypes.string.isRequired,
  }).isRequired,
};

export default VaseCarousel;
